/**
 * Constantes et utilitaires pour les catégories de produits e-commerce
 *
 * Ce fichier centralise toutes les données de catégories utilisées dans l'application :
 * - Catégories principales
 * - Sous-catégories par catégorie
 * - Tags suggérés par catégorie
 * - Icônes associées à chaque catégorie
 *
 * @module productCategories
 */

import React from 'react';
import {
  UserIcon,
  SparklesIcon,
  DevicePhoneMobileIcon,
  HomeIcon,
  HeartIcon,
  BookOpenIcon,
  ShoppingBagIcon,
  CubeIcon,
  TruckIcon
} from '@heroicons/react/24/outline';

/**
 * Liste des 14 catégories principales de produits e-commerce
 * @constant {string[]}
 */
export const categories = [
  'Mode Femme',
  'Mode Homme',
  'Mode Enfant',
  'Beauté et Cosmétiques',
  'Électronique',
  'Électroménager',
  'Animaux de Compagnie',
  'Telephone et accessoires',
  'Maison et Jardin',
  'Sport et Loisirs',
  'Automobile',
  'Livres et Médias',
  'Alimentation',
  'Santé et Bien-être',
  'Bijoux et Accessoires'
];

/**
 * Sous-catégories organisées par catégorie principale
 * Chaque catégorie contient 10 sous-catégories spécifiques
 * @constant {Object.<string, string[]>}
 */
export const subCategories = {
  'Mode Femme': [
    'Robes',
    'Tops et Blouses',
    'Pantalons et Jeans',
    'Jupes',
    'Vestes et Manteaux',
    'Lingerie et Sous-vêtements',
    'Chaussures Femme',
    'Sacs à Main',
    'Accessoires Mode',
    'Maillots de Bain'
  ],
  'Mode Homme': [
    'T-shirts et Polos',
    'Chemises',
    'Pantalons et Jeans',
    'Vestes et Manteaux',
    'Costumes',
    'Sous-vêtements',
    'Chaussures Homme',
    'Accessoires Homme',
    'Ceintures et Portefeuilles',
    'Cravates et Nœuds Papillon'
  ],
  'Mode Enfant': [
    'Vêtements Bébé (0-2 ans)',
    'Vêtements Fille (3-16 ans)',
    'Vêtements Garçon (3-16 ans)',
    'Chaussures Enfant',
    'Accessoires Enfant',
    'Cartables et Sacs',
    'Déguisements',
    'Vêtements de Sport Enfant',
    'Pyjamas et Sous-vêtements',
    'Manteaux et Vestes Enfant'
  ],
  'Beauté et Cosmétiques': [
    'Maquillage',
    'Soins du Visage',
    'Soins du Corps',
    'Parfums Femme',
    'Parfums Homme',
    'Soins des Cheveux',
    'Produits de Rasage',
    'Vernis à Ongles',
    'Accessoires Beauté',
    'Cosmétiques Bio'
  ],
  'Électronique': [
    'Smartphones',
    'Ordinateurs Portables',
    'Tablettes',
    'TV et Audio',
    'Appareils Photo',
    'Consoles de Jeux',
    'Casques et Écouteurs',
    'Accessoires Tech',
    'Montres Connectées',
    'Drones'
  ],
  'Électroménager': [
    'Réfrigérateurs',
    'Lave-linge',
    'Lave-vaisselle',
    'Fours et Micro-ondes',
    'Aspirateurs',
    'Cafetières',
    'Robots de Cuisine',
    'Fer à Repasser',
    'Climatisation',
    'Petit Électroménager'
  ],
  'Telephone et accessoires': [
    'Smartphones',
    'Tablettes',
    'Accessoires Smartphones',
    'Accessoires Tablettes',
    'Accessoires Smartwatches',
  ],
  'Animaux de Compagnie': [
    'Alimentation Chiens',
    'Alimentation Chats',
    'Jouets pour Animaux',
    'Accessoires Chiens',
    'Accessoires Chats',
    'Aquariophilie',
    'Oiseaux',
    'Rongeurs',
    'Hygiène et Soins',
    'Transport Animaux'
  ],
  'Maison et Jardin': [
    'Meubles',
    'Décoration',
    'Literie',
    'Cuisine et Arts de la Table',
    'Éclairage',
    'Jardin et Plantes',
    'Bricolage et Outils',
    'Rangement',
    'Textiles de Maison',
    'Sécurité Maison'
  ],
  'Sport et Loisirs': [
    'Fitness et Musculation',
    'Running et Jogging',
    'Cyclisme',
    'Sports d\'Équipe',
    'Sports Nautiques',
    'Sports d\'Hiver',
    'Randonnée et Camping',
    'Tennis et Badminton',
    'Golf',
    'Arts Martiaux'
  ],
  'Automobile': [
    'Pièces Auto',
    'Accessoires Intérieur',
    'Accessoires Extérieur',
    'Entretien Auto',
    'Pneumatiques',
    'GPS et Navigation',
    'Audio Auto',
    'Sécurité Auto',
    'Tuning',
    'Moto'
  ],
  'Livres et Médias': [
    'Romans',
    'BD et Mangas',
    'Livres Enfant',
    'Livres Scolaires',
    'DVD et Blu-ray',
    'Musique CD et Vinyles',
    'Livres Numériques',
    'Magazines',
    'Jeux de Société',
    'Puzzles'
  ],
  'Alimentation': [
    'Fruits et Légumes',
    'Viandes et Poissons',
    'Produits Laitiers',
    'Épicerie Sucrée',
    'Épicerie Salée',
    'Boissons',
    'Surgelés',
    'Bio et Naturel',
    'Produits du Monde',
    'Vins et Spiritueux'
  ],
  'Santé et Bien-être': [
    'Compléments Alimentaires',
    'Matériel Médical',
    'Produits de Parapharmacie',
    'Bien-être et Relaxation',
    'Aromathérapie',
    'Optique',
    'Audition',
    'Matériel de Sport Santé',
    'Produits Minceur',
    'Hygiène Intime'
  ],
  'Bijoux et Accessoires': [
    'Bijoux Femme',
    'Bijoux Homme',
    'Montres Femme',
    'Montres Homme',
    'Piercing',
    'Bijoux Fantaisie',
    'Bijoux Précieux',
    'Lunettes de Soleil',
    'Chapeaux et Casquettes',
    'Écharpes et Foulards'
  ]
};

/**
 * Tags suggérés par catégorie pour faciliter le référencement produit
 * Contient 10 tags pertinents pour chaque catégorie
 * @constant {Object.<string, string[]>}
 */
export const suggestionsByCategory = {
  'Mode Femme': ['tendance', 'élégant', 'confortable', 'mode', 'féminin', 'casual', 'chic', 'stylé', 'moderne', 'fashion'],
  'Mode Homme': ['masculin', 'décontracté', 'élégant', 'business', 'casual', 'moderne', 'classique', 'tendance', 'style', 'confort'],
  'Mode Enfant': ['enfant', 'confortable', 'ludique', 'coloré', 'pratique', 'résistant', 'amusant', 'école', 'jeu', 'grandir'],
  'Beauté et Cosmétiques': ['beauté', 'soin', 'naturel', 'bio', 'anti-âge', 'hydratant', 'parfumé', 'doux', 'efficace', 'luxe'],
  'Électronique': ['high-tech', 'innovant', 'performant', 'connecté', 'intelligent', 'rapide', 'moderne', 'digital', 'wireless', 'HD'],
  'Électroménager': ['efficace', 'économique', 'silencieux', 'compact', 'performant', 'durable', 'pratique', 'moderne', 'facile', 'énergie'],
  'Telephone et accessoires': ['smartphone', 'tablette', 'smartwatch', 'accessoire', 'smartphone', 'tablette', 'smartwatch', 'accessoire'],
  'Animaux de Compagnie': ['animal', 'compagnie', 'santé', 'nutrition', 'amusant', 'confort', 'sécurité', 'hygiène', 'bien-être', 'premium'],
  'Maison et Jardin': ['décoration', 'confort', 'design', 'pratique', 'moderne', 'cosy', 'élégant', 'fonctionnel', 'style', 'qualité'],
  'Sport et Loisirs': ['sport', 'fitness', 'performance', 'entraînement', 'santé', 'plein-air', 'équipement', 'pro', 'loisir', 'actif'],
  'Automobile': ['auto', 'voiture', 'performance', 'sécurité', 'confort', 'style', 'pratique', 'qualité', 'résistant', 'fiable'],
  'Livres et Médias': ['lecture', 'culture', 'divertissement', 'éducatif', 'passion', 'collection', 'bestseller', 'découverte', 'savoir', 'plaisir'],
  'Alimentation': ['bio', 'naturel', 'frais', 'savoureux', 'santé', 'nutrition', 'gourmet', 'local', 'artisanal', 'qualité'],
  'Santé et Bien-être': ['santé', 'bien-être', 'naturel', 'bio', 'soin', 'relaxation', 'forme', 'vitalité', 'équilibre', 'thérapeutique'],
  'Bijoux et Accessoires': ['bijou', 'élégant', 'luxe', 'tendance', 'précieux', 'style', 'mode', 'raffiné', 'cadeau', 'brillant']
};

/**
 * Mapping des icônes Heroicons pour chaque catégorie
 * Utilisé pour l'affichage visuel dans les interfaces
 * @constant {Object.<string, React.Component>}
 */
export const categoryIconMap = {
  'Mode Femme': UserIcon,
  'Mode Homme': UserIcon,
  'Mode Enfant': HeartIcon,
  'Beauté et Cosmétiques': SparklesIcon,
  'Électronique': DevicePhoneMobileIcon,
  'Électroménager': HomeIcon,
  'Telephone et accessoires': DevicePhoneMobileIcon,
  'Animaux de Compagnie': HeartIcon,
  'Maison et Jardin': HomeIcon,
  'Sport et Loisirs': CubeIcon,
  'Automobile': TruckIcon,
  'Livres et Médias': BookOpenIcon,
  'Alimentation': ShoppingBagIcon,
  'Santé et Bien-être': HeartIcon,
  'Bijoux et Accessoires': SparklesIcon
};

/**
 * Retourne le composant icône JSX pour une catégorie donnée
 * @param {string} category - Nom de la catégorie
 * @returns {JSX.Element} Composant icône avec className h-4 w-4
 * @example
 * getCategoryIcon('Mode Femme') // <UserIcon className="h-4 w-4" />
 */
export const getCategoryIcon = (category) => {
  const IconComponent = categoryIconMap[category] || CubeIcon;
  return <IconComponent className="h-4 w-4" />;
};

/**
 * Retourne les tags suggérés pour une catégorie donnée, filtrés pour exclure les tags déjà utilisés
 * @param {string} category - Nom de la catégorie
 * @param {string[]} existingTags - Tags déjà ajoutés au produit (pour filtrage)
 * @returns {string[]} Liste des tags suggérés disponibles
 * @example
 * getSuggestedTags('Mode Femme', ['tendance', 'chic']) // ['élégant', 'confortable', 'mode', ...]
 */
export const getSuggestedTags = (category, existingTags = []) => {
  const categoryTags = suggestionsByCategory[category] || [];
  // Retourner seulement les tags qui ne sont pas déjà ajoutés
  return categoryTags.filter(tag => !existingTags.includes(tag));
};
